import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useVendorAuth } from '../../context/VendorAuthContext';
import { setSessionWithExpiry } from '../helpers/sessionUtils';
import SEO from "../../components/common/SEO";
import './VendorLogin.css';

const LOGIN_RESOURCES = [
    { type: 'link', rel: 'stylesheet', href: '/assets/vendor/font-awesome/css/fontawesome-all.min.css', id: 'login-page-fa' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.googleapis.com', id: 'login-page-gfont-preconnect1' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'true', id: 'login-page-gfont-preconnect2' },
    { type: 'link', rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght=0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900&display=swap', id: 'login-page-gfont-style' }
];

const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    const alertStyle = {
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '500',
        marginBottom: '20px',
        textAlign: 'left',
        border: '1px solid transparent',
    };
    const style = type === 'error' ? {
        ...alertStyle,
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        borderColor: '#fca5a5'
    } : (type === 'success' ? {
        ...alertStyle,
        backgroundColor: '#e6f9f3',
        color: '#0bc184',
        borderColor: '#a7f3d0'
    } : {
        ...alertStyle,
        backgroundColor: '#eff6ff',
        color: '#25a5f0',
        borderColor: '#bfdbfe'
    });
    return (
        <div 
            className="form-message-alert"
            style={style} 
            role="alert" 
            dangerouslySetInnerHTML={{ __html: message }} 
        />
    );
};

function Login() {
    const navigate = useNavigate();
    const { login } = useVendorAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        document.title = "Login - Mangoloops";
        LOGIN_RESOURCES.forEach(attrs => {
            if (!document.getElementById(attrs.id)) {
                const el = document.createElement(attrs.type);
                Object.keys(attrs).forEach(key => { if (key !== 'type') el[key] = attrs[key]; });
                document.head.appendChild(el);
            }
        });
        return () => {
            LOGIN_RESOURCES.forEach(attrs => {
                const el = document.getElementById(attrs.id);
                if (el) el.remove();
            });
        };
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormMessage({ text: '', type: '' });

        if (!email || !password) {
            setFormMessage({ text: "Please enter email and password.", type: "error" });
            setIsLoading(false);
            return;
        }

        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/vendors/login`;
            const response = await axios.post(apiUrl, { email, password });
            const { user, token, message } = response.data;

            setFormMessage({ text: message || "Login successful!", type: "success" });
            setSessionWithExpiry(response.data);
            login(token, user);
        } catch (err) {
            const errorData = err.response?.data;
            const errorMessage = errorData?.message || "Login failed. Please check your credentials.";

            if (errorData?.status === 'not_verified_redirect_to_registration') {
                setFormMessage({ text: errorMessage, type: "info" });
                setTimeout(() => navigate(`/admin/kyc-verification`), 2000);
            } else {
                setFormMessage({ text: errorMessage, type: "error" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <SEO title="Vendor Login | Mangoloops" description="Login as a vendor on Mangoloops to list your hotels, cabs, and holiday packages." />

            <div className="login-page-body">
                <div className="portal-wrapper">

                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo-mangoloops.png" alt="MangoLoops Logo" />
                        </Link>
                    </div>

                    <div className="auth-card">
                        <div id="loginPassPanel" className="auth-panel active">
                            <div className="header">
                                <h2>Welcome back!</h2>
                                <p>Connect. Get Leads. Grow Your Travel Business</p>
                            </div>

                            <FormMessageDisplay message={formMessage.text} type={formMessage.type} />

                            <form onSubmit={handleLogin}>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        placeholder="Email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#6b7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0
                                        }}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>

                                <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>

                                <button type="submit" className="btn btn-gray-login" disabled={isLoading}>
                                    {isLoading ? 'Logging in...' : 'Login'}
                                </button>
                            </form>

                            <div className="or-separator">or</div>

                            <button type="button" className="btn btn-blue-action" onClick={() => navigate('/login-otp')}>
                                🔒 Login with Mobile Number
                            </button>

                            <div className="footer-switch-text">
                                Don’t have an account? <Link to="/registration">Sign Up</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Login;
